// ============================================================================
// CHANGO V5.0 — Activate Trial Page (§9.6.2c 阶段 2：试用激活)
// ============================================================================
// URL 带 ?sku_code=xxx 进来
// 4 必填项每行红 `*`：套餐选择（顶部展示用户选的）/ 营业执照 / 手机号 / 短信验证码
// 手机号默认填 user.phone
// 点"获取验证码"调 POST /api/sms/send-code，scope='trial_activation'
// 验证码 5 分钟有效 + 60s 倒计时
// 4 项齐全才能"提交激活"
// 提交调 POST /api/workspace/activate-trial body {sku_code, license_file_url, phone, sms_code}
// 成功 → 跳 /app/dashboard（自动 active + 7 天试用倒计时）
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import { brandForSku } from '../../lib/skuBrandVersion.js';
import SmsCodeInput from '../../components/SmsCodeInput.jsx';

const schema = z.object({
  sku_code: z.string().min(1, '请选择套餐'),
  license_file_url: z.string().min(1, '请上传营业执照'),
  phone: z.string().regex(/^1[3-9]\d{9}$/, '请输入中国大陆手机号'),
  sms_code: z.string().regex(/^\d{6}$/, '短信验证码是 6 位数字')
});

export default function ActivateTrial() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedLicenseUrl, setUploadedLicenseUrl] = useState('');

  const skuCode = searchParams.get('sku_code');
  const brand = brandForSku(skuCode);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      sku_code: skuCode || '',
      license_file_url: '',
      phone: user?.phone || '',
      sms_code: ''
    }
  });

  const licenseInput = watch('license_file_url');
  const phoneValue = watch('phone');

  // 营业执照上传
  const handleLicenseUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 校验文件大小 ≤ 5MB
    if (file.size > 5 * 1024 * 1024) {
      setServerError('营业执照文件大小不能超过 5MB');
      return;
    }

    setUploadingFile(true);
    setServerError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.upload('/api/upload/license', formData);
      const licenseUrl = res.url || res.license_file_url;
      if (!licenseUrl) throw new Error('上传接口未返回执照 URL');
      setUploadedLicenseUrl(licenseUrl);
      setValue('license_file_url', licenseUrl, { shouldValidate: true });
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : '上传失败';
      setServerError(msg);
    } finally {
      setUploadingFile(false);
    }
  };

  // 发送短信验证码
  const handleSendSmsCode = async ({ phone, scope }) => {
    const res = await api.post('/api/sms/send-code', { phone, scope });
    return res;
  };

  const onSubmit = async (values) => {
    setServerError(null);
    setSubmitting(true);
    try {
      await api.post('/api/workspace/activate-trial', {
        sku_code: values.sku_code,
        license_file_url: values.license_file_url,
        phone: values.phone,
        sms_code: values.sms_code
      });
      // 激活成功跳 /app/dashboard
      navigate('/app/dashboard');
    } catch (err) {
      if (err instanceof HttpError) {
        setServerError(err.message || '激活失败');
      } else {
        setServerError(err?.message || '网络错误，请重试');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!skuCode) {
    return (
      <div className="min-h-screen bg-slate-50 py-12 px-4">
        <div className="mx-auto w-full max-w-2xl">
          <div className="alert alert-error">
            <p>未指定套餐，请从套餐页重新选择</p>
            <button
              type="button"
              onClick={() => navigate('/app/welcome')}
              className="btn btn-sm btn-outline mt-2"
            >
              返回选择套餐
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="mx-auto w-full max-w-2xl px-4">
        <h1 className="mb-2 text-2xl font-semibold text-slate-800">激活试用</h1>
        <p className="mb-8 text-sm text-slate-500">
          完成以下步骤即可开始 7 天免费试用，期间可使用全部功能。
        </p>

        {serverError ? (
          <div role="alert" className="alert-error mb-6">{serverError}</div>
        ) : null}

        {/* 套餐展示卡 */}
        <div className="mb-6 rounded-lg border-2 border-chango-300 bg-chango-50 p-4">
          <p className="text-sm text-slate-500 mb-2">已选择套餐</p>
          <h2 className="text-lg font-semibold text-chango-700">{brand.shortName}</h2>
          <p className="text-sm text-slate-600 mt-2">{brand.description}</p>
        </div>

        <form noValidate onSubmit={handleSubmit(onSubmit)} className="card space-y-6 p-8">
          <input type="hidden" {...register('sku_code')} />

          {/* 营业执照上传 */}
          <div>
            <label className="label">
              <span className="text-red-600">*</span> 营业执照
            </label>
            <p className="text-sm text-slate-500 mb-3">
              PNG/JPG 格式，单文件 ≤ 5MB。请上传清晰的营业执照照片。
            </p>
            <div className="rounded border-2 border-dashed border-slate-300 p-4">
              {uploadedLicenseUrl ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-slate-700">已上传营业执照</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedLicenseUrl('');
                      setValue('license_file_url', '', { shouldValidate: true });
                    }}
                    className="text-sm text-slate-500 hover:text-slate-700"
                  >
                    重新上传
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 cursor-pointer text-slate-600 hover:text-slate-800">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="text-sm">
                    {uploadingFile ? '上传中…' : '点击或拖拽上传营业执照'}
                  </span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={handleLicenseUpload}
                    disabled={uploadingFile}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            {errors.license_file_url ? <p className="err">{errors.license_file_url.message}</p> : null}
          </div>

          {/* 手机号 + 短信验证码（用 SmsCodeInput 组件）*/}
          <SmsCodeInput
            phone={phoneValue}
            phoneError={errors.phone?.message}
            codeValue={watch('sms_code')}
            codeError={errors.sms_code?.message}
            onPhoneChange={(val) => setValue('phone', val, { shouldValidate: true })}
            onCodeChange={(val) => setValue('sms_code', val, { shouldValidate: true })}
            onSendCode={handleSendSmsCode}
            scope="trial_activation"
            disabled={false}
          />

          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => navigate('/app/welcome')}
              className="text-sm text-chango-600 hover:underline"
            >
              返回选择套餐
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? <span className="spinner" aria-hidden="true" /> : null}
              {submitting ? '激活中…' : '提交激活'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
